import { TextField } from "@mui/material";
import PropTypes from "prop-types";
import { useOtp } from "context/OtpContext";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import { updateCustomerAPI } from "layouts/profile/_request";
import { useAuth } from "context";
import { AnalyticsEvents } from "core/constant";
import { useAnalyticsEvent } from "hooks/useAnalyticsEvent";

const KnowMeForm = ({ setLevel }) => {
  const { isLoading } = useOtp();
  const { showSnackbarWithMessage, saveAuthData } = useAuth();

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
  });
  const [phoneError, setPhoneError] = useState("");

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");

  const { trackCustomEvent } = useAnalyticsEvent();

  // Handle input changes for all fields
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const formatPhoneNumber = (value) => {
    value = value.replace(/\D/g, ""); // Remove all non-digit characters
    if (value.length > 10) value = value.slice(0, 10);

    if (!value || value.length === 0) return "";
    if (value.length <= 3) return `(${value}`;
    if (value.length <= 6) return `(${value.slice(0, 3)}) ${value.slice(3)}`;
    return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
  };

  const handlePhoneChange = (event) => {
    const formattedValue = formatPhoneNumber(event.target.value);
    setFormData((prevData) => ({
      ...prevData,
      phoneNumber: formattedValue,
    }));

    const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (formattedValue && !phonePattern.test(formattedValue)) {
      setPhoneError("Phone number must be in the format (XXX) XXX-XXXX");
    } else {
      setPhoneError("");
    }
  };

  const handleFormFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "firstName") {
      if (value.trim().length < 3) {
        setFirstNameError("First name should be at least 3 characters.");
      } else {
        setFirstNameError("");
      }
    }

    if (name === "lastName") {
      if (value && value.trim().length < 3) {
        setLastNameError("Last name should be at least 3 characters.");
      } else {
        setLastNameError("");
      }
    }
  };

  // Handle form submission
  const handleSubmitUpdate = async (e) => {
    e.preventDefault();

    try {
      const {
        data: { success, message, data },
      } = await updateCustomerAPI({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
      });
      if (success) {
        const verifiedCustomer = await saveAuthData(data?.tokens);

        trackCustomEvent(
          AnalyticsEvents.SIGNUP,
          {
            method: "otp",
            auth_origin: "customer_portal",
            user_id: verifiedCustomer.id,
            user_role: verifiedCustomer.role,
            user_type: verifiedCustomer.userType,
          },
          false
        );
        navigate("/dashboard");
        showSnackbarWithMessage({
          type: "success",
          message: "Success! Welcome to Catering Rewards!",
        });
      } else {
        showSnackbarWithMessage({
          type: "error",
          message: message,
        });
      }
    } catch (error) {
      console.log("error", error);
      showSnackbarWithMessage({
        type: "error",
        message: error.message,
      });
    }
  };

  return (
    <>
      <MDBox>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="firstName"
          label="First Name"
          name="firstName"
          placeholder="Enter your First name"
          FormHelperTextProps={{
            sx: { color: "#475467" },
          }}
          value={formData.firstName}
          onChange={handleFormFieldChange}
          error={!!firstNameError} // Display error if firstNameError exists
          helperText={firstNameError}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#a9a9a9",
              },
              "&:hover fieldset": {
                borderColor: "#a9a9a9",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#808080",
              },
            },
          }}
        />
      </MDBox>
      <MDBox>
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          id="lastName"
          label="Last Name"
          placeholder="Enter your Last name"
          name="lastName"
          autoComplete="family-name"
          FormHelperTextProps={{
            sx: { color: "#475467" },
          }}
          value={formData.lastName}
          onChange={handleFormFieldChange}
          error={!!lastNameError} // Display error if nameError exists
          helperText={lastNameError}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#a9a9a9",
              },
              "&:hover fieldset": {
                borderColor: "#a9a9a9",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#808080",
              },
            },
          }}
        />
      </MDBox>
      <MDBox>
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          id="phoneNumber"
          label="Phone Number"
          placeholder="Enter your phone number"
          name="phoneNumber"
          autoComplete="tel"
          helperText={phoneError}
          error={!!phoneError}
          FormHelperTextProps={{
            sx: { color: "#475467" },
          }}
          value={formData.phoneNumber}
          onChange={handlePhoneChange}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#a9a9a9",
              },
              "&:hover fieldset": {
                borderColor: "#a9a9a9",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#808080",
              },
            },
          }}
        />
      </MDBox>

      <MDBox
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        sx={{ columnGap: "0.2em" }}
        mt={3}
        color="#4caf50"
      >
        <MDTypography sx={{ fontWeight: 500 }} variant="button" color="#4caf50" textGradient>
          By clicking &quot;Continue&quot;, you agree to our
        </MDTypography>
        <MDTypography
          component="a"
          target="_blank"
          href="https://cateringrewards.com/terms_of_use/"
          variant="button"
          sx={{ color: "#4caf50", textDecoration: "underline", fontWeight: 500 }}
        >
          Terms of Use
        </MDTypography>
        <MDTypography sx={{ fontWeight: 500 }} variant="button" color="#4caf50" textGradient>
          and
        </MDTypography>
        <MDTypography
          component="a"
          target="_blank"
          href="https://cateringrewards.com/privacy_policy/"
          variant="button"
          sx={{ color: "#4caf50", textDecoration: "underline", fontWeight: 500 }}
        >
          Privacy Policy.
        </MDTypography>
      </MDBox>

      <MDBox mt={"0px"} display="flex" gap="10px">
        <MDButton
          type="button"
          variant="outlined"
          color="dark"
          style={{
            width: "100%",
            marginTop: "1rem",
            borderRadius: 8,
          }}
          fullWidth
          onClick={() => setLevel(0)}
        >
          Back
        </MDButton>
        <MDButton
          onClick={(e) => handleSubmitUpdate(e)}
          variant="contained"
          color="success"
          style={{
            width: "100%",
            marginTop: "1rem",
            borderRadius: 8,
          }}
          disabled={isLoading || !formData.firstName}
          fullWidth
        >
          Continue
        </MDButton>
      </MDBox>
    </>
  );
};

export default KnowMeForm;

KnowMeForm.propTypes = {
  setLevel: PropTypes.func,
};
